import { useState, useMemo } from "react";
import { useAdminLeads } from "../hooks/useAdminLeads";
import { Seo } from "../components/Seo";
import { AdminGate } from "../components/admin/AdminGate";
import { AdminHeader, AdminStats, AdminFilters } from "../components/admin/AdminToolbar";
import { BulkActionBar } from "../components/admin/BulkActionBar";
import { LeadCard } from "../components/admin/LeadCard";
import { ReplyDrawer } from "../components/admin/ReplyDrawer";
import { followUpReason, sortByUrgency } from "../lib/followUp";

const matches = (lead, query, filter) => {
  if (filter !== "all" && lead.status !== filter) return false;
  const q = query.toLowerCase();
  if (!q) return true;
  return [lead.name, lead.email, lead.organization, lead.region, lead.waterbody_type]
    .some((v) => (v || "").toLowerCase().includes(q));
};

const useSelection = () => {
  const [selected, setSelected] = useState(() => new Set());
  const select = (id, on) => setSelected((prev) => { const n = new Set(prev); on ? n.add(id) : n.delete(id); return n; });
  const selectAll = (ids) => setSelected(new Set(ids));
  const clear = () => setSelected(new Set());
  return { selected, select, selectAll, clear };
};

const LeadList = ({ leads, actions, onReply, selection }) => (
  <div className="mt-4 space-y-4" data-testid="admin-leads-list">
    {leads.length === 0 && (
      <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-slate-600" data-testid="admin-no-leads">
        No leads found.
      </div>
    )}
    {leads.map((lead) => (
      <LeadCard
        key={lead.id}
        lead={lead}
        onStatus={actions.setStatus}
        onReply={onReply}
        onAddNote={actions.addNote}
        onFollowUp={actions.setFollowUp}
        onDelete={actions.removeLead}
        selected={selection.selected.has(lead.id)}
        onSelect={selection.select}
      />
    ))}
  </div>
);

export default function AdminLeads() {
  const admin = useAdminLeads();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [needsFollowUp, setNeedsFollowUp] = useState(false);
  const [replyLead, setReplyLead] = useState(null);
  const selection = useSelection();

  const followUpCount = useMemo(() => admin.leads.filter((l) => followUpReason(l)).length, [admin.leads]);
  const filtered = useMemo(() => {
    const base = admin.leads.filter((l) => matches(l, query, filter));
    return needsFollowUp ? sortByUrgency(base.filter((l) => followUpReason(l))) : base;
  }, [admin.leads, query, filter, needsFollowUp]);
  const currentReplyLead = replyLead ? admin.leads.find((l) => l.id === replyLead.id) || replyLead : null;

  return (
    <AdminGate page="admin" authed={admin.authed} login={admin.login}>
      <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <Seo page="admin" />
        <div className="max-w-7xl mx-auto">
          <AdminHeader loading={admin.loading} onRefresh={admin.load} onLogout={admin.logout} />
          <AdminStats stats={admin.stats} />
          <AdminFilters
            query={query} setQuery={setQuery} filter={filter} setFilter={setFilter}
            needsFollowUp={needsFollowUp} setNeedsFollowUp={setNeedsFollowUp} followUpCount={followUpCount}
          />
          <BulkActionBar
            selected={selection.selected}
            filteredIds={filtered.map((l) => l.id)}
            onSelectAll={selection.selectAll}
            onClear={selection.clear}
            onDelete={admin.removeLeads}
          />
          <LeadList leads={filtered} actions={admin} onReply={setReplyLead} selection={selection} />
        </div>
        <ReplyDrawer
          key={replyLead?.id || "none"}
          lead={currentReplyLead}
          open={!!replyLead}
          onOpenChange={(o) => { if (!o) setReplyLead(null); }}
          onStatus={admin.setStatus}
          onSend={admin.sendReply}
        />
      </div>
    </AdminGate>
  );
}
